import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare, ShoppingBag, Calendar, Bell, Wallet, Heart,
  Plane, Music, FileText, Smile, Headphones, Camera, Map,
  Bot, Zap, Shield, Clock, Users, Star, Check, X, ArrowRight,
  Sparkles, Brain, Globe, Mic, Image as ImageIcon, CreditCard, Key, Ticket,
  List, StickyNote, ChevronDown, ChevronRight, Database, BarChart2,
  Code2, Rss, Package, Store, Search, Layers, BookOpen, Phone
} from 'lucide-react';

const useAuthState = () => {
  const token = localStorage.getItem('okbot_token') || sessionStorage.getItem('okbot_token');
  return { isAuthenticated: !!token };
};

const FEATURES = [
  {
    id: 'chat', icon: MessageSquare, emoji: '🤖',
    title: 'Asistente IA Conversacional', tagline: 'Tu IA personal siempre disponible',
    description: 'Chat inteligente con múltiples modelos de IA (GPT-4, Claude, Gemini). Entiende contexto complejo, genera imágenes, responde con voz y aprende de tus preferencias.',
    highlights: ['30+ modelos de IA', 'Voz bidireccional', 'Memoria de contexto', 'Generación de imágenes'],
    color: 'red', gradient: 'from-red-500 to-rose-600',
    image: 'https://images.pexels.com/photos/8438922/pexels-photo-8438922.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'marketplace', icon: ShoppingBag, emoji: '🛍️',
    title: 'Marketplace Inteligente', tagline: 'Compara precios como un experto',
    description: 'Compara precios en múltiples tiendas en tiempo real. Guarda favoritos, recibe alertas de ofertas y obtén recomendaciones personalizadas con IA.',
    highlights: ['Comparación en tiempo real', 'Alertas de ofertas', 'Historial de precios', 'Favoritos y listas'],
    color: 'green', gradient: 'from-green-500 to-emerald-600',
    image: 'https://images.pexels.com/photos/5632398/pexels-photo-5632398.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'calendar', icon: Calendar, emoji: '📅',
    title: 'Calendario Inteligente', tagline: 'Organiza tu tiempo con IA',
    description: 'Calendario interactivo con vistas día/semana/mes. Crea eventos por voz, sincroniza con Google Calendar, recibe recordatorios inteligentes y detecta conflictos automáticamente.',
    highlights: ['Vista interactiva grande', 'Creación por voz', 'Sincronización Google/Apple', 'Detección de conflictos'],
    color: 'blue', gradient: 'from-blue-500 to-indigo-600',
    image: 'https://images.pexels.com/photos/733856/pexels-photo-733856.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'notes', icon: StickyNote, emoji: '📝',
    title: 'Notas con Voz', tagline: 'Captura ideas al instante',
    description: 'Crea notas por voz o texto con categorías, etiquetas y búsqueda avanzada. Exporta a PDF o comparte al instante. OkBot organiza y resume tus notas automáticamente.',
    highlights: ['Notas por voz', 'Organización automática', 'Búsqueda inteligente', 'Exportación PDF'],
    color: 'yellow', gradient: 'from-yellow-500 to-amber-600',
    image: 'https://images.pexels.com/photos/1925536/pexels-photo-1925536.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'reminders', icon: Bell, emoji: '🔔',
    title: 'Recordatorios Inteligentes', tagline: 'Nunca olvides lo importante',
    description: 'Crea recordatorios por voz o texto con recurrencia, ubicación y prioridad. OkBot aprende tus hábitos y te avisa en el momento perfecto.',
    highlights: ['Creación por voz', 'Recurrencia avanzada', 'Recordatorios por ubicación', 'Prioridad inteligente'],
    color: 'orange', gradient: 'from-orange-500 to-red-500',
    image: 'https://images.pexels.com/photos/1028741/pexels-photo-1028741.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'lists', icon: List, emoji: '✅',
    title: 'Listas Inteligentes', tagline: 'Organiza todo en listas dinámicas',
    description: 'Crea listas de compras, tareas, viajes, películas y más. Comparte listas con amigos, crea por voz y deja que la IA las organice y priorice.',
    highlights: ['Creación por voz', '20+ categorías', 'Listas compartidas', 'Priorización IA'],
    color: 'teal', gradient: 'from-teal-500 to-cyan-600',
    image: 'https://images.pexels.com/photos/3243090/pexels-photo-3243090.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'finance', icon: Wallet, emoji: '💰',
    title: 'Finanzas Personales', tagline: 'Control total de tu dinero',
    description: 'Gestión completa de gastos, ingresos, presupuestos e inversiones. Gráficas en tiempo real, seguimiento de deudas y análisis inteligente de tus finanzas.',
    highlights: ['Gráficas en tiempo real', 'Control de presupuesto', 'Seguimiento de inversiones', 'Análisis IA'],
    color: 'emerald', gradient: 'from-emerald-500 to-green-600',
    image: 'https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'health', icon: Heart, emoji: '❤️',
    title: 'Salud & Bienestar', tagline: 'Tu salud, nuestra prioridad',
    description: 'Identifica medicamentos con tu cámara, busca doctores por especialidad y país, guarda recetas médicas, planifica tus comidas y rutinas de ejercicio con IA.',
    highlights: ['Identificación de meds con cámara', 'Buscador de doctores global', 'Planificador de comidas IA', 'Seguimiento de sueño'],
    color: 'pink', gradient: 'from-pink-500 to-rose-600',
    image: 'https://images.pexels.com/photos/4498606/pexels-photo-4498606.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'travel', icon: Plane, emoji: '✈️',
    title: 'Viajes Inteligentes', tagline: 'Planifica el viaje perfecto',
    description: 'Busca vuelos, hoteles y renta de autos. Genera itinerarios completos con IA exportables a PDF. Calcula rutas con OpenStreetMap y solicita Uber, DiDi o Taxi directamente.',
    highlights: ['Búsqueda de vuelos y hoteles', 'Itinerarios IA exportables', 'Mapas con rutas', 'Pide chofer integrado'],
    color: 'cyan', gradient: 'from-cyan-500 to-blue-600',
    image: 'https://images.pexels.com/photos/1532771/pexels-photo-1532771.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'music', icon: Music, emoji: '🎵',
    title: 'Música & Entretenimiento', tagline: 'Tu experiencia musical completa',
    description: 'Identifica canciones al instante, busca artistas con fotos y videos de YouTube, crea playlists personalizadas y conecta con Spotify, Apple Music, Amazon Music y YouTube Music.',
    highlights: ['Identificación de canciones', 'Videos de YouTube embebidos', 'Conexión con Spotify y más', 'Playlists personalizadas'],
    color: 'indigo', gradient: 'from-indigo-500 to-purple-600',
    image: 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'documents', icon: FileText, emoji: '📄',
    title: 'Documentos IA', tagline: 'Genera cualquier documento al instante',
    description: 'Crea contratos, CVs, informes, presentaciones y más con IA. Exporta a PDF, Word o comparte directamente. Templates profesionales para cualquier necesidad.',
    highlights: ['100+ tipos de documentos', 'Exportación PDF/Word', 'Templates profesionales', 'Edición en tiempo real'],
    color: 'orange', gradient: 'from-orange-500 to-amber-600',
    image: 'https://images.pexels.com/photos/3771097/pexels-photo-3771097.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'memes', icon: Smile, emoji: '😂',
    title: 'Generador de Memes', tagline: 'Crea y comparte memes virales',
    description: 'Genera memes con plantillas populares, texto personalizado e IA. Comparte al instante en redes sociales o con amigos dentro de la plataforma.',
    highlights: ['500+ plantillas', 'Generación con IA', 'Compartir en redes', 'Texto personalizado'],
    color: 'violet', gradient: 'from-violet-500 to-purple-600',
    image: 'https://images.pexels.com/photos/3943882/pexels-photo-3943882.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'support', icon: Headphones, emoji: '🎧',
    title: 'Soporte IA 24/7', tagline: 'Siempre estamos contigo',
    description: 'Agentes de soporte IA disponibles 24/7 con sistema de tickets. Atención personalizada para usuarios y negocios con seguimiento completo de solicitudes.',
    highlights: ['Soporte 24/7', 'Sistema de tickets', 'Seguimiento en tiempo real', 'Agentes especializados'],
    color: 'red', gradient: 'from-red-400 to-rose-500',
    image: 'https://images.pexels.com/photos/3820788/pexels-photo-3820788.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop',
    isNew: true
  },
  {
    id: 'voice', icon: Mic, emoji: '🎙️',
    title: 'Comandos de Voz', tagline: 'Habla, OkBot escucha',
    description: 'Control total por voz. Dicta notas, crea recordatorios, envía mensajes y navega por la app con comandos de voz en español. Respuestas por voz con síntesis natural.',
    highlights: ['Control total por voz', 'Síntesis de voz natural', 'Comandos en español', 'Manos libres'],
    color: 'rose', gradient: 'from-rose-500 to-red-600',
    image: 'https://images.pexels.com/photos/3379934/pexels-photo-3379934.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop'
  },
  {
    id: 'business', icon: Store, emoji: '🏪',
    title: 'Panel de Negocios', tagline: 'Tu negocio, potenciado con IA',
    description: 'Dashboard completo para negocios con chatbot IA personalizado, analytics en tiempo real, gestión de horarios y automaciones, soporte a clientes y widgets embebibles.',
    highlights: ['Chatbot IA para tu negocio', 'Analytics en tiempo real', 'Horarios & automaciones', 'Widgets web embebibles'],
    color: 'blue', gradient: 'from-blue-500 to-sky-600',
    image: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop',
    isNew: true
  },
  {
    id: 'knowledge', icon: Database, emoji: '🔌',
    title: '72 APIs de Conocimiento', tagline: 'Acceso a toda la información',
    description: 'Conecta con 72+ APIs: YouTube, Spotify, Google, Pexels, OpenWeather, IMDb, Twitch, GitHub, Wikipedia, CoinGecko, PubMed, y muchas más. Activa las que necesites con un clic.',
    highlights: ['72+ APIs disponibles', 'YouTube, Spotify, Google', 'Clima, Finanzas, Salud', 'Activación con un clic'],
    color: 'purple', gradient: 'from-purple-500 to-violet-600',
    image: 'https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop',
    isNew: true
  },
  {
    id: 'widgets', icon: Code2, emoji: '🧩',
    title: 'Widgets Embebibles', tagline: 'OkBot en cualquier web',
    description: 'Genera widgets de chat personalizados para tu sitio web con un solo clic. 5 tipos diferentes (Bot, OkUser, OkBusiness, Soporte), temas claro/oscuro y código embed listo para usar.',
    highlights: ['5 tipos de widget', 'Temas claro/oscuro', 'Código embed automático', 'Preview en tiempo real'],
    color: 'teal', gradient: 'from-teal-500 to-emerald-600',
    image: 'https://images.pexels.com/photos/270360/pexels-photo-270360.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop',
    isNew: true
  },
  {
    id: 'sso', icon: Key, emoji: '🔑',
    title: 'Acceso Universal SSO', tagline: 'Un acceso para todo',
    description: 'OAuth SSO con Google, Microsoft y Bubble.io. Un solo login para acceder desde cualquier plataforma o aplicación conectada. Sincroniza usuarios automáticamente.',
    highlights: ['Google OAuth', 'Microsoft SSO', 'Bubble.io integrado', 'API para desarrolladores'],
    color: 'indigo', gradient: 'from-indigo-400 to-violet-500',
    image: 'https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop',
    isNew: true
  },
  {
    id: 'payments', icon: CreditCard, emoji: '💳',
    title: 'Pagos Seguros', tagline: 'Suscripciones sin complicaciones',
    description: 'Pagos seguros con Stripe. Gestiona tu suscripción, actualiza métodos de pago y accede a funciones premium con facturación automática.',
    highlights: ['Pagos con Stripe', 'Facturación automática', 'Múltiples planes', 'Historial de pagos'],
    color: 'blue', gradient: 'from-blue-400 to-indigo-500',
    image: 'https://images.pexels.com/photos/4968391/pexels-photo-4968391.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop',
    isNew: true
  },
  {
    id: 'web-scraping', icon: Search, emoji: '🌐',
    title: 'Web Scraping IA Profundo', tagline: 'Cualquier sitio se vuelve conocimiento',
    description: 'Registra una URL y OkBot rastrea automáticamente la home y subpáginas (mismo dominio, hasta profundidad 3, máx 30 páginas), extrae el texto limpio y lo resume con IA. Tus agentes consultan ese conocimiento al instante.',
    highlights: ['Crawler recursivo seguro', 'Resumen automático con IA', 'Protección SSRF y TLS', 'Listo para tus agentes'],
    color: 'cyan', gradient: 'from-cyan-500 to-blue-600',
    image: 'https://images.pexels.com/photos/270632/pexels-photo-270632.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop',
    isNew: true
  },
  {
    id: 'wa-audio', icon: Phone, emoji: '🎙️',
    title: 'WhatsApp con Audios', tagline: 'Habla por WhatsApp, OkBot te entiende',
    description: 'Envía notas de voz por WhatsApp y OkBot las transcribe con Gemini multimodal y responde como si las hubieras escrito. Crea recordatorios, agenda eventos y consulta tu información, todo hablando.',
    highlights: ['Transcripción Gemini multimodal', 'Funciona con todas las herramientas', 'Reintentos automáticos', 'Privacidad end-to-end'],
    color: 'emerald', gradient: 'from-emerald-500 to-teal-600',
    image: 'https://images.pexels.com/photos/4126724/pexels-photo-4126724.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop',
    isNew: true
  },
  {
    id: 'expert-agents', icon: Bot, emoji: '🧠',
    title: 'Agentes Expertos por Sección', tagline: 'Un especialista en cada módulo',
    description: 'OkBot Mercader (Marketplace), Secretario (Notas/Calendario), Financiero, Doctor, Agente de Viajes, Melómano, Profesor (Docs) y Cinéfilo. Cada uno con chat embebido, banner y micrófono flotante.',
    highlights: ['8 agentes especialistas', 'Chat minimizable y mic flotante', 'Misma memoria que el principal', 'Delegación automática'],
    color: 'purple', gradient: 'from-purple-500 to-fuchsia-600',
    image: 'https://images.pexels.com/photos/8438923/pexels-photo-8438923.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop',
    isNew: true
  },
  {
    id: 'mcp-rapidapi', icon: Layers, emoji: '🛠️',
    title: 'MCP + RapidAPI Integrados', tagline: 'Acciones reales, no solo respuestas',
    description: 'Tus agentes ejecutan herramientas vía MCP (calendario, notas, finanzas, salud, listas) y consultan RapidAPI Hub para datos en tiempo real. Si la herramienta falla, OkBot lo dice — no se inventa confirmaciones.',
    highlights: ['Function-calling nativo', 'Tools de calendario, finanzas, salud', 'RapidAPI Hub conectado', 'Honestidad operativa'],
    color: 'orange', gradient: 'from-orange-500 to-red-500',
    image: 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop',
    isNew: true
  },
  {
    id: 'public-api', icon: BookOpen, emoji: '📘',
    title: 'API Pública Documentada', tagline: 'Conecta cualquier plataforma',
    description: 'Documentación interactiva tipo Swagger y ReDoc disponible públicamente en /api-docs. Más de 200 endpoints REST, esquema OpenAPI 3.0 descargable y guía de integración para Bubble.io, Zapier y más.',
    highlights: ['Swagger UI público', 'ReDoc interactivo', 'OpenAPI 3.0 JSON', 'Listo para Bubble/Zapier'],
    color: 'indigo', gradient: 'from-indigo-500 to-blue-600',
    image: 'https://images.pexels.com/photos/3861958/pexels-photo-3861958.jpeg?auto=compress&cs=tinysrgb&w=800&h=500&fit=crop',
    isNew: true
  },
];

const COMPARISON = [
  { feature: 'Chat IA Conversacional', okbot: true, memorae: true },
  { feature: 'Generación de Imágenes IA', okbot: true, memorae: false },
  { feature: 'Marketplace con Comparador de Precios', okbot: true, memorae: false },
  { feature: 'Calendario Interactivo con Voz', okbot: true, memorae: true },
  { feature: 'Notas & Recordatorios por Voz', okbot: true, memorae: true },
  { feature: 'Finanzas Personales + Gráficas', okbot: true, memorae: true },
  { feature: 'Salud con Identificación por Cámara', okbot: true, memorae: false },
  { feature: 'Viajes: Vuelos, Hoteles, Chofer', okbot: true, memorae: false },
  { feature: 'Itinerarios IA exportables a PDF', okbot: true, memorae: false },
  { feature: 'Rutas con OpenStreetMap', okbot: true, memorae: false },
  { feature: 'Música: Identificar + YouTube + Spotify', okbot: true, memorae: false },
  { feature: 'Generador de Documentos IA', okbot: true, memorae: false },
  { feature: 'Generador de Memes', okbot: true, memorae: false },
  { feature: 'Dashboard para Negocios', okbot: true, memorae: false },
  { feature: 'Widgets Embebibles para tu web', okbot: true, memorae: false },
  { feature: 'API + Documentación completa', okbot: true, memorae: false },
  { feature: 'Soporte IA 24/7 con Tickets', okbot: true, memorae: false },
  { feature: 'Pagos con Stripe integrado', okbot: true, memorae: false },
  { feature: 'OAuth SSO (Google, Microsoft, Bubble)', okbot: true, memorae: false },
  { feature: '72 APIs de Conocimiento', okbot: true, memorae: false },
];

const STATS = [
  { value: '19+', label: 'Módulos', sublabel: 'integrados' },
  { value: '72', label: 'APIs', sublabel: 'de conocimiento' },
  { value: '30+', label: 'Modelos IA', sublabel: 'disponibles' },
  { value: '24/7', label: 'Soporte', sublabel: 'disponible' },
];

const colorMap = {
  red:     { bg: 'bg-red-50',     border: 'border-red-200',     icon: 'text-red-500',     dot: 'bg-red-500',     badge: 'bg-red-50 text-red-600 border border-red-100' },
  green:   { bg: 'bg-green-50',   border: 'border-green-200',   icon: 'text-green-600',   dot: 'bg-green-500',   badge: 'bg-green-50 text-green-700 border border-green-100' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    icon: 'text-blue-600',    dot: 'bg-blue-500',    badge: 'bg-blue-50 text-blue-700 border border-blue-100' },
  yellow:  { bg: 'bg-yellow-50',  border: 'border-yellow-200',  icon: 'text-yellow-600',  dot: 'bg-yellow-500',  badge: 'bg-yellow-50 text-yellow-700 border border-yellow-100' },
  orange:  { bg: 'bg-orange-50',  border: 'border-orange-200',  icon: 'text-orange-600',  dot: 'bg-orange-500',  badge: 'bg-orange-50 text-orange-700 border border-orange-100' },
  teal:    { bg: 'bg-teal-50',    border: 'border-teal-200',    icon: 'text-teal-600',    dot: 'bg-teal-500',    badge: 'bg-teal-50 text-teal-700 border border-teal-100' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-600', dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border border-emerald-100' },
  pink:    { bg: 'bg-pink-50',    border: 'border-pink-200',    icon: 'text-pink-600',    dot: 'bg-pink-500',    badge: 'bg-pink-50 text-pink-700 border border-pink-100' },
  cyan:    { bg: 'bg-cyan-50',    border: 'border-cyan-200',    icon: 'text-cyan-600',    dot: 'bg-cyan-500',    badge: 'bg-cyan-50 text-cyan-700 border border-cyan-100' },
  indigo:  { bg: 'bg-indigo-50',  border: 'border-indigo-200',  icon: 'text-indigo-600',  dot: 'bg-indigo-500',  badge: 'bg-indigo-50 text-indigo-700 border border-indigo-100' },
  violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  icon: 'text-violet-600',  dot: 'bg-violet-500',  badge: 'bg-violet-50 text-violet-700 border border-violet-100' },
  rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    icon: 'text-rose-600',    dot: 'bg-rose-500',    badge: 'bg-rose-50 text-rose-700 border border-rose-100' },
  purple:  { bg: 'bg-purple-50',  border: 'border-purple-200',  icon: 'text-purple-600',  dot: 'bg-purple-500',  badge: 'bg-purple-50 text-purple-700 border border-purple-100' },
};

const SuperPoderesPage = () => {
  const { isAuthenticated } = useAuthState();
  const [activeFeature, setActiveFeature] = useState(null);
  const [showAllComparison, setShowAllComparison] = useState(false);

  const visibleComparison = showAllComparison ? COMPARISON : COMPARISON.slice(0, 10);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#ffffff', color: '#0f172a' }}>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-screen flex items-center" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 60%, #fef2f2 100%)' }}>
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(239,68,68,0.06) 0%, transparent 60%),
                            radial-gradient(circle at 80% 20%, rgba(139,92,246,0.04) 0%, transparent 50%),
                            linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)`,
          backgroundSize: 'auto, auto, 60px 60px, 60px 60px'
        }} />

        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 py-20 text-center">
          <div className="flex justify-center mb-10">
            <img src="/okbot_light_theme.png" alt="OkBot" className="w-20 h-20 object-contain" />
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-8"
            style={{ background: 'rgba(239,68,68,0.08)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.2)' }}>
            <Sparkles size={14} />
            Sistema Operativo Personal con IA
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight tracking-tight" style={{ color: '#0f172a' }}>
            Los{' '}
            <span style={{ background: 'linear-gradient(135deg, #ef4444 0%, #f97316 50%, #ec4899 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Super Poderes
            </span>
            <br />de OkBot
          </h1>

          <p className="text-lg md:text-xl mb-12 max-w-2xl mx-auto leading-relaxed" style={{ color: '#64748b' }}>
            19 módulos de inteligencia artificial integrados para organizar, potenciar y transformar tu vida cotidiana.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            {!isAuthenticated ? (
              <Link to="/" className="px-8 py-4 rounded-2xl font-semibold text-white transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', boxShadow: '0 8px 32px rgba(239,68,68,0.25)' }}>
                Activar mis Super Poderes <ArrowRight size={18} className="inline ml-1" />
              </Link>
            ) : (
              <Link to="/" className="px-8 py-4 rounded-2xl font-semibold text-white transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', boxShadow: '0 8px 32px rgba(239,68,68,0.25)' }}>
                Ir al Dashboard <ArrowRight size={18} className="inline ml-1" />
              </Link>
            )}
            <a href="#poderes" className="px-8 py-4 rounded-2xl font-semibold transition-all hover:scale-105"
              style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#334155' }}>
              Ver todos los poderes
            </a>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {STATS.map((s, i) => (
              <div key={i} className="rounded-2xl p-5 text-center" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div className="text-3xl font-bold mb-1" style={{ color: '#ef4444' }}>{s.value}</div>
                <div className="text-sm font-semibold" style={{ color: '#1e293b' }}>{s.label}</div>
                <div className="text-xs" style={{ color: '#94a3b8' }}>{s.sublabel}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES GRID ──────────────────────────────────────────────────── */}
      <section id="poderes" className="py-24" style={{ background: '#f8fafc' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4"
              style={{ background: 'rgba(239,68,68,0.08)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.15)' }}>
              {FEATURES.length} MÓDULOS INTEGRADOS
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#0f172a' }}>Todo lo que necesitas, aquí</h2>
            <p style={{ color: '#64748b' }}>Cada módulo potenciado por Inteligencia Artificial avanzada</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {FEATURES.map((feature) => {
              const Icon = feature.icon;
              const colors = colorMap[feature.color] || colorMap.red;
              const isActive = activeFeature === feature.id;
              return (
                <div
                  key={feature.id}
                  onClick={() => setActiveFeature(isActive ? null : feature.id)}
                  className="rounded-2xl p-5 cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-md"
                  style={{
                    background: isActive ? 'rgba(239,68,68,0.04)' : '#ffffff',
                    border: isActive ? '1px solid rgba(239,68,68,0.3)' : '1px solid #e2e8f0',
                    boxShadow: isActive ? '0 4px 16px rgba(239,68,68,0.08)' : '0 1px 4px rgba(0,0,0,0.04)',
                  }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${colors.bg}`}>
                      <Icon size={20} className={colors.icon} />
                    </div>
                    <div className="flex items-center gap-2">
                      {feature.isNew && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white">NUEVO</span>
                      )}
                      <span className="text-xl">{feature.emoji}</span>
                    </div>
                  </div>
                  <h3 className="font-semibold text-sm mb-1 leading-snug" style={{ color: '#0f172a' }}>{feature.title}</h3>
                  <p className="text-xs mb-3" style={{ color: '#64748b' }}>{feature.tagline}</p>

                  {isActive && (
                    <div className="mt-3 space-y-3">
                      <p className="text-xs leading-relaxed" style={{ color: '#475569' }}>{feature.description}</p>
                      <div className="space-y-1">
                        {feature.highlights.map((h, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs" style={{ color: '#475569' }}>
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${colors.dot}`} />
                            {h}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!isActive && (
                    <div className="flex gap-1 flex-wrap">
                      {feature.highlights.slice(0, 2).map((h, i) => (
                        <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${colors.badge}`}>{h}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FEATURE SHOWCASE ──────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: '#ffffff' }}>
        <div className="max-w-5xl mx-auto px-6 space-y-24">
          {FEATURES.slice(0, 6).map((feature, idx) => {
            const Icon = feature.icon;
            const colors = colorMap[feature.color] || colorMap.red;
            const isLeft = idx % 2 === 0;
            return (
              <div key={feature.id} className={`flex flex-col ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'} gap-12 items-center`}>
                <div className="flex-1 space-y-5">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${colors.badge}`}>
                    <Icon size={12} />
                    {feature.title}
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold leading-tight" style={{ color: '#0f172a' }}>{feature.tagline}</h3>
                  <p className="leading-relaxed" style={{ color: '#475569' }}>{feature.description}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {feature.highlights.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <Check size={11} className="text-green-600" />
                        </div>
                        {h}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="w-64 h-64 rounded-3xl overflow-hidden relative shadow-lg" style={{ border: '1px solid #e2e8f0' }}>
                    <img src={feature.image} alt={feature.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                    <div className={`absolute bottom-4 left-4 w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center shadow`}>
                      <Icon size={20} className={colors.icon} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── COMPARISON TABLE ──────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: '#f8fafc' }}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4"
              style={{ background: 'rgba(239,68,68,0.08)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.15)' }}>
              COMPARATIVA
            </span>
            <h2 className="text-4xl font-bold mb-3" style={{ color: '#0f172a' }}>OkBot vs. Memorae</h2>
            <p style={{ color: '#64748b' }}>Descubre por qué OkBot es la opción más completa</p>
          </div>

          <div className="rounded-3xl overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0', background: '#ffffff' }}>
            <div className="grid grid-cols-[1fr_auto_auto] gap-4 p-5" style={{ background: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
              <div className="text-sm font-semibold" style={{ color: '#64748b' }}>Funcionalidad</div>
              <div className="text-center w-24">
                <div className="inline-flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-red-500 flex items-center justify-center">
                    <Bot size={12} className="text-white" />
                  </div>
                  <span className="font-bold text-sm" style={{ color: '#0f172a' }}>OkBot</span>
                </div>
              </div>
              <div className="text-center w-24">
                <span className="font-semibold text-sm" style={{ color: '#94a3b8' }}>Memorae</span>
              </div>
            </div>

            {visibleComparison.map((item, idx) => (
              <div
                key={idx}
                className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-4 transition-colors"
                style={{
                  background: idx % 2 === 0 ? '#fafafa' : '#ffffff',
                  borderBottom: '1px solid #f1f5f9'
                }}
              >
                <div className="text-sm" style={{ color: '#334155' }}>{item.feature}</div>
                <div className="text-center w-24 flex justify-center">
                  {item.okbot ? (
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                      <Check size={12} className="text-green-600" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                      <X size={12} className="text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="text-center w-24 flex justify-center">
                  {item.memorae ? (
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                      <Check size={12} className="text-gray-500" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center">
                      <X size={12} className="text-gray-300" />
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div className="p-4 text-center" style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <button
                onClick={() => setShowAllComparison(p => !p)}
                className="flex items-center gap-2 mx-auto text-sm font-medium transition-colors hover:text-red-500"
                style={{ color: '#64748b' }}
              >
                {showAllComparison ? 'Ver menos' : `Ver ${COMPARISON.length - 10} funciones más`}
                <ChevronDown size={16} className={`transition-transform ${showAllComparison ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          <div className="mt-8 p-5 rounded-2xl text-center" style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.12)' }}>
            <p style={{ color: '#475569' }}>
              OkBot incluye <span className="font-bold text-red-500">{COMPARISON.filter(c => c.okbot).length} funcionalidades</span> vs{' '}
              <span className="font-bold" style={{ color: '#94a3b8' }}>{COMPARISON.filter(c => c.memorae).length} de Memorae</span>
            </p>
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      {!isAuthenticated && (
        <section className="py-24" style={{ background: '#ffffff' }}>
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="p-12 rounded-3xl shadow-lg" style={{ background: 'linear-gradient(135deg, #fef2f2 0%, #f0f9ff 100%)', border: '1px solid #e2e8f0' }}>
              <div className="text-5xl mb-6">⚡</div>
              <h2 className="text-4xl font-bold mb-4" style={{ color: '#0f172a' }}>¿Listo para tener Super Poderes?</h2>
              <p className="mb-8" style={{ color: '#64748b' }}>Únete y accede a los {FEATURES.length} módulos de IA en un solo lugar.</p>
              <Link to="/"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-white transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', boxShadow: '0 8px 32px rgba(239,68,68,0.3)' }}>
                <Zap size={20} />Activar mis Super Poderes
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <footer className="py-10 text-center" style={{ borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 mb-4" style={{ fontSize: '13px' }}>
            <a href="/terminos" style={{ color: '#94a3b8' }} className="hover:text-red-500 transition-colors">Términos de Servicio</a>
            <a href="/privacidad" style={{ color: '#94a3b8' }} className="hover:text-red-500 transition-colors">Política de Privacidad</a>
            <a href="/aviso-legal" style={{ color: '#94a3b8' }} className="hover:text-red-500 transition-colors">Aviso Legal</a>
            <a href="/cookies" style={{ color: '#94a3b8' }} className="hover:text-red-500 transition-colors">Cookies</a>
          </div>
          <p style={{ color: '#cbd5e1', fontSize: '13px' }}>© 2026 OkBot — Tu Sistema Operativo Personal con IA.</p>
        </div>
      </footer>
    </div>
  );
};

export default SuperPoderesPage;
